import {
  Button,
  Form,
  Modal,
  Select,
  Menu,
  Dropdown,
  Tag,
  Input,
  message as AntdMessage,
  Tooltip,
  Popover,
  Checkbox,
} from "antd";
import Table from "../../core/common/dataTable/index";
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { all_routes } from "../router/all_routes";
import {
  getAllTickets,
  Ticket,
  getAllEnquiries,
} from "../../core/api/useTickets";
import { toast } from "react-toastify";
import { getAllTasks, Task, updateTaskStatus } from "../../core/api/usetasks";
import { fetchProductList, ModuleType } from "../../core/api/useCompanies";
import { fetchModules, TableEnquiryRow } from "../../core/api/useEnquiryStatus";
import useGetEmployees, { Employee } from "../../core/api/useGetEmployees";
import { useSelector } from "react-redux";
import { API_BASE_PATH } from "../../environment";
import { useAuthToken } from "../../core/data/redux/useAuthToken";
import {
  exportSingleTaskExcel,
  exportAllTasksExcel,
} from "../../core/api/useTaskExport";
import {
  DownOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  EyeOutlined,
  ExportOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { usePermission } from "../../core/api/useRolePermission";
import { CategoryResponse, getAllCategories } from "../../core/api/category";

const Tasks: React.FC = () => {
  const routes = all_routes;
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const { employees } = useGetEmployees();
  const role = useSelector((state: any) => state.auth.role);
  const EmpId = useSelector((state: any) => state.auth.employeeId);
  const empToken = useAuthToken();
  const modalRef = useRef<HTMLDivElement>(null);
  const canRead = usePermission("Tasks", "read");
  const canWrite = usePermission("Tasks", "write");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [reason, setReason] = useState<string>("");

  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<string>("");
  const [confirmTask, setConfirmTask] = useState<any | null>(null);
  const empRole = useSelector((state: any) => state.auth.role);
  const empDept = useSelector((state: any) => state.auth.empDept);
  const [selectedManagerId, setSelectedManagerId] = useState(null);
  const [selectedEscalateTo, setSelectedEscalateTo] = useState<string | null>(
    null
  );
  const [raisedTicketModalVisible, setRaisedTicketModalVisible] =
    useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [enquiries, setEnquiries] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [categories, setCategories] = useState<CategoryResponse[]>([]);
  const [modules, setModules] = useState<ModuleType[]>([]);
  // Filter states
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    taskName: [] as string[],
    ticketId: [] as string[],
    priority: [] as string[],
    category: [] as string[],
  });
  const [modalFilters, setModalFilters] = useState({
    taskName: [] as string[],
    ticketId: [] as string[],
    priority: [] as string[],
    category: [] as string[],
  });

  const showModal = () => {
    setModalFilters({
      taskName: filters.taskName,
      ticketId: filters.ticketId,
      priority: filters.priority,
      category: filters.category,
    });
    setFilterModalVisible(true);
  };

  const roleFilter = "Manager";
  const filteredEmployees = (employees || []).filter(
    (emp) => emp.role_name?.toLowerCase() === roleFilter.toLowerCase()
  );

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleTaskSelectionChange = (
    selectedRowKeys: React.Key[],
    selectedRows: any[]
  ) => {
    const selectedIDs = selectedRows.map((row) => row.task_id);
    setSelectedTaskIds(selectedIDs);
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const data = await getAllTasks(empToken);
      setTasks(Array.isArray(data) ? data : []);
      if (!Array.isArray(data)) toast.warning("No tasks found");
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const refreshTasks = async () => {
    try {
      setLoading(true);
      const data = await getAllTasks(empToken);

      if (Array.isArray(data)) {
        setTasks(data);
      } else {
        setTasks([]);
        toast.warning("No tasks found");
      }
    } catch (error) {
      console.error(error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshTasks();

    const intervalId = setInterval(() => {
      refreshTasks();
    }, 2000);

    return () => clearInterval(intervalId);
  }, []);

  const fetchTickets = async () => {
    if (!canRead) {
      setTickets([]);
      return;
    }
    try {
      const data = await getAllTickets("");
      setTickets(data);
    } catch (err) {
      console.error("Error fetching tickets", err);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      const data = await getAllCategories(empToken);
      // ✅ Always make sure it's an array
      setCategories(Array.isArray(data) ? data : []);
    };
    fetchCategories();
  }, [empToken]);

  // Memoized category map
  const categoryMap = useMemo(() => {
    const map: Record<number, string> = {};
    if (Array.isArray(categories)) {
      categories.forEach((cat) => {
        map[cat.cate_id] = cat.cate_name;
      });
    }
    return map;
  }, [categories]);

  const employeeNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    employees.forEach((emp) => {
      map[emp.employee_id] = `${emp.firstname} ${emp.lastname}`;
    });
    return map;
  }, [employees]);

  const productMap = useMemo(() => {
    if (!Array.isArray(products)) return {};
    const map: Record<number, { name: string; modules: string }> = {};
    for (const product of products) {
      const modulesArray = Array.isArray(product.modules)
        ? product.modules
        : [];
      map[product.product_id] = {
        name: product.product_name,
        modules: modulesArray.map((m: ModuleType) => m.module_name).join(","),
      };
    }
    return map;
  }, [products]);

  const moduleMap = useMemo(() => {
    if (!Array.isArray(products)) return {};
    const map: Record<number, string> = {};
    products.forEach((product) => {
      const modulesArray = Array.isArray(product.modules)
        ? product.modules
        : [];
      modulesArray.forEach((module: ModuleType) => {
        map[module.module_id] = module.module_name;
      });
    });
    return map;
  }, [products]);

  useEffect(() => {
    if (!canRead) {
      setModules([]);
      return;
    }
    fetchModules(empToken)
      .then((data) => setModules(data))
      .catch((err) => console.error("Error loading modules", err))
      .finally(() => setLoading(false));
  }, [canRead, empToken]);

  const loadProducts = useCallback(async () => {
    if (!canRead) {
      setProducts([]);
      return;
    }
    try {
      const data = await fetchProductList(empToken);
      setProducts(Array.isArray(data) ? data : []);
    } catch {}
  }, [canRead, empToken]);

  useEffect(() => {
    if (!canRead) {
      setEnquiries([]);
      setProducts([]);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        const [enquiriesData, productsData] = await Promise.all([
          getAllEnquiries(empToken),
          fetchProductList(empToken),
        ]);
        setEnquiries(enquiriesData);
        setProducts(Array.isArray(productsData) ? productsData : []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [canRead, empToken]);

  // Filter options
  const taskNameOptions = useMemo(() => {
    const uniqueSet = new Set(tasks.map((t) => t.task_name).filter(Boolean));
    const unique = Array.from(uniqueSet).sort();
    return unique.map((name) => ({ label: name, value: name }));
  }, [tasks]);

  const ticketIdOptions = useMemo(() => {
    const uniqueSet = new Set(tasks.map((t) => t.ticket_id).filter(Boolean));
    const unique = Array.from(uniqueSet).sort();
    return unique.map((id) => ({ label: id, value: id }));
  }, [tasks]);

  const priorityOptions = useMemo(
    () => [
      { label: "High", value: "High" },
      { label: "Medium", value: "Medium" },
      { label: "Low", value: "Low" },
    ],
    []
  );

  const categoryOptions = useMemo(() => {
    const uniqueSet = new Set(
      tasks.map((t) => t.ticket?.enquiry?.category_date?.cate_name || "N/A")
    );
    const unique = Array.from(uniqueSet).sort();
    return unique.map((cat) => ({ label: cat, value: cat }));
  }, [tasks]);

  const activeFiltersCount = useMemo(
    () =>
      Object.values(filters).reduce(
        (sum, arr) => sum + (arr as string[]).length,
        0
      ),
    [filters]
  );

  const clearFilters = () => {
    setModalFilters({
      taskName: [],
      ticketId: [],
      priority: [],
      category: [],
    });
  };

  const filteredTasks = useMemo(() => {
    let result = tasks;

    if (searchText) {
      result = result.filter((task) => {
        const searchableFields = [
          task.task_name,
          task.ticket_id,
          employeeNameMap[task.assigned_employee] || task.assigned_employee,
          employeeNameMap[task.creator_employee] || task.creator_employee,
          task.priority,
          task.status,
          task.ticket?.enquiry?.category_date?.cate_name,
        ];

        return searchableFields.some((field) =>
          (field || "")
            .toString()
            .toLowerCase()
            .includes(searchText.toLowerCase())
        );
      });
    }

    // Apply filters
    if (filters.taskName.length > 0) {
      result = result.filter((task) =>
        filters.taskName.includes(task.task_name)
      );
    }
    if (filters.ticketId.length > 0) {
      result = result.filter((task) =>
        filters.ticketId.includes(task.ticket_id)
      );
    }
    if (filters.priority.length > 0) {
      result = result.filter((task) =>
        filters.priority.includes(task.priority)
      );
    }
    if (filters.category.length > 0) {
      result = result.filter((task) =>
        filters.category.includes(
          task.ticket?.enquiry?.category_date?.cate_name || "N/A"
        )
      );
    }

    return result;
  }, [tasks, searchText, filters, employeeNameMap]);

  const statusOptions = useMemo(() => {
    if (role === "Employee" && empDept !== "Testing") {
      return [
        { label: "In Progress", value: "In Progress" },
        { label: "In Review", value: "In Review" },
        { label: "On Hold", value: "On Hold" },
        { label: "Testing", value: "Testing" },
        { label: "Testing Completed", value: "Testing Completed" },
        { label: "Rework", value: "Rework" },
      ];
    } else if (empDept === "Testing") {
      return [
        { label: "Testing Completed", value: "Testing Completed" },
        { label: "Rework", value: "Rework" },
      ];
    } else if (role === "Admin" || role === "Manager" || role === "Incharge") {
      return [
        { label: "Rework", value: "Rework" },
        { label: "Completed", value: "Completed" },
      ];
    }
  }, [role]);

  const handleStatusChange = useCallback(
    (newStatus: string, record: any) => {
      if (!canWrite) {
        toast.warning("You don't have permission");
        return;
      }
      if (newStatus === "Testing Completed" && role === "Employee") {
        setConfirmStatus(newStatus);
        setConfirmTask(record);
        setSelectedEscalateTo(null);
        setIsConfirmModalVisible(true);
      } else if (["Rework", "On Hold", "In Review"].includes(newStatus)) {
        setSelectedTask(record);
        setSelectedStatus(newStatus);
        setReason("");
        setIsModalVisible(true);
      } else {
        setConfirmStatus(newStatus);
        setConfirmTask(record);
        setIsConfirmModalVisible(true);
      }
    },
    [empDept, canWrite]
  );

  const submitStatusChange = async (
    record: any,
    newStatus: string,
    escalateToManagerId?: string | null,
    customReason?: string
  ) => {
    if (!canWrite) {
      toast.warning("You don't have permission");
      return;
    }
    try {
      const payload = {
        ticket_id: record.ticket_id,
        status: newStatus,
        reason: customReason || null,
        priority: record.priority,
        employee_id: EmpId,
        employee_name: employeeNameMap[EmpId] ?? "",
        escalate_to: escalateToManagerId || selectedManagerId,
      };

      await updateTaskStatus(empToken, record.task_id, payload);

      setTasks((prev) =>
        prev.map((task) =>
          task.task_id === record.task_id
            ? { ...task, status: newStatus, reason: customReason || "" }
            : task
        )
      );
    } catch (err: any) {
      const apiError = err?.response?.data;

      const rawMessage = apiError?.details || apiError?.message || "";

      if (rawMessage.includes("You already have an ongoing task in progress")) {
        toast.warning(
          "You already have a task in progress. Please hold it before starting a new one."
        );
      } else {
        console.error("Status update failed:", err);
      }
    }
  };

  const handleModalOk = async () => {
    if (!canWrite) {
      toast.warning("You don't have permission");
      return;
    }
    if (!reason.trim()) {
      AntdMessage.error("Reason is required");
      return;
    }
    if (selectedTask) {
      await submitStatusChange(selectedTask, selectedStatus, undefined, reason);
    }
    setIsModalVisible(false);
  };

  const assignedToColumn = {
    title: "Assigned To",
    dataIndex: "assigned_employee",
    key: "assigned_to",
    fixed: "left",
    width: 170,
    sorter: (a: any, b: any) =>
      (employeeNameMap[a.assigned_employee] || "").localeCompare(
        employeeNameMap[b.assigned_employee] || ""
      ),
    render: (id: string) => employeeNameMap[id] || id,
  };

  const downloadFile = async (filename: string) => {
    try {
      const response = await fetch(`${API_BASE_PATH}/enquiries/download/${encodeURIComponent(filename)}`, {
        headers: {
          Authorization: `Bearer ${empToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const taskColumns = useMemo(
    () => [
      {
        title: "Task Name",
        dataIndex: "task_name",
        key: "task_name",
        fixed: "left",
        width: 250,
        sorter: (a: any, b: any) =>
          (a.task_name || "").localeCompare(b.task_name || ""),
      },
      {
        title: "Ticket ID",
        dataIndex: "ticket_id",
        key: "ticket_id",
        fixed: "left",
        width: 120,
        sorter: (a: any, b: any) =>
          (a.ticket_id || "").localeCompare(b.ticket_id || ""),
        render: (_: any, record: any) => (
          <div className="d-flex align-items-center justify-content-between">
            <span>{record.ticket_id || "-"}</span>
            {record.ticket && (
              <Popover
                content="Task History"
                trigger="hover"
                overlayInnerStyle={{
                  backgroundColor: "#b7a4dcff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                }}
              >
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canRead) {
                      toast.warning("You don't have permission");
                      return;
                    }
                    setSelectedTicket(record.ticket);
                    setSelectedTask(record);
                    setRaisedTicketModalVisible(true);
                  }}
                  style={{
                    marginLeft: 8,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    backgroundColor: "#e6f4ff",
                    cursor: "pointer",
                    transition: "all 0.3s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#bae7ff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e6f4ff")
                  }
                >
                  <EyeOutlined style={{ fontSize: 14, color: "#1890ff" }} />
                </span>
              </Popover>
            )}
          </div>
        ),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        fixed: "left",
        width: 180,
        sorter: (a: any, b: any) => {
          const descA = a.ticket?.description || "";
          const descB = b.ticket?.description || "";
          return descA.localeCompare(descB);
        },
        render: (_: any, record: any) => {
          const description = record.ticket?.description || "No description";
          const maxLength = 20;
          const displayText =
            description.length > maxLength
              ? `${description.slice(0, maxLength)}...`
              : description;

          return (
            <div
              style={{
                fontSize:
                  window.innerWidth < 576
                    ? "10px"
                    : window.innerWidth < 768
                    ? "11px"
                    : "12px",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                whiteSpace: "normal",
                display: "block",
                maxWidth: "100%",
              }}
            >
              {displayText}
            </div>
          );
        },
      },
      ...(role?.toLowerCase() !== "employee" ? [assignedToColumn] : []),
      {
        title: "Priority",
        dataIndex: "priority",
        key: "priority",
        width: 120,
        sorter: (a: any, b: any) =>
          (a.priority || "").localeCompare(b.priority || ""),
        render: (priority: string) => {
          const colorMap = {
            High: "red",
            Medium: "orange",
            Low: "green",
          };
          return (
            <Tag color={colorMap[priority as keyof typeof colorMap]}>
              {priority}
            </Tag>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 180,
        sorter: (a: any, b: any) =>
          (a.status || "").localeCompare(b.status || ""),
        render: (status: string, record: any) => (
          <Tooltip title={`Click to set reason for ${status}`}>
            <Select
              value={status}
              style={{ width: 150 }}
              onChange={(value) => handleStatusChange(value, record)}
              options={statusOptions}
            />
          </Tooltip>
        ),
      },
      {
        title: "Estimated Hours",
        dataIndex: "estimated_hours",
        key: "estimated_hours",
        width: 170,
        sorter: (a: any, b: any) => a.estimated_hours - b.estimated_hours,
        render: (text: number) => (
          <div style={{ textAlign: "center" }}>{text}</div>
        ),
      },
      {
        title: "Total Hours",
        dataIndex: "total_hours",
        key: "total_hours",
        width: 150,
        sorter: (a: any, b: any) => a.total_hours - b.total_hours,
        render: (seconds: number) => {
          const totalMinutes = Math.floor(seconds / 60);
          const hrs = Math.floor(totalMinutes / 60);
          const mins = totalMinutes % 60;
          return (
            <div style={{ textAlign: "center" }}>{`${hrs}h ${mins}m`}</div>
          );
        },
      },
      {
        title: "Due Date",
        dataIndex: "due_date",
        key: "due_date",
        width: 150,
        sorter: (a: any, b: any) =>
          new Date(a.due_date).getTime() - new Date(b.due_date).getTime(),
      },
      {
        title: "Created By",
        dataIndex: "creator_employee",
        key: "created_by",
        width: 180,
        sorter: (a: any, b: any) =>
          (employeeNameMap[a.created_by] || "").localeCompare(
            employeeNameMap[b.created_by] || ""
          ),
      },
      {
        title: "Category",
        key: "category",
        width: 150,
        render: (_: any, record: any) => {
          // const categoryId = record.ticket?.enquiry?.category;
          const categoryName =
            record.ticket?.enquiry?.category_date?.cate_name || "N/A";

          const categoryColorMap: Record<string, string> = {
            Billable: "bg-success-transparent",
            "Non Billable": "bg-warning-transparent",
            "Future Development": "bg-info-transparent",
            "Fame of Company": "bg-pink-transparent",
            "N/A": "bg-secondary-transparent",
          };

          const badgeClass =
            categoryColorMap[categoryName] || "bg-secondary-transparent";

          return (
            <span
              className={`badge ${badgeClass}`}
              style={{ textTransform: "capitalize" }}
            >
              {categoryName}
            </span>
          );
        },
      },
      {
        title: "Uploaded Files",
        key: "uploaded_files",
        width: 150,
        render: (_: any, record: any) => {
          const enquiryFiles =
            record.ticket?.enquiry?.upload_file
              ?.split(",")
              .map((file: string) => file.trim()) || [];

          const getFileIcon = (filename: string) => {
            const ext = filename.split(".").pop()?.toLowerCase() || "";
            if (["png", "jpg", "jpeg", "gif", "bmp", "svg"].includes(ext))
              return "🖼️";
            if (["pdf"].includes(ext)) return "📄";
            if (["doc", "docx"].includes(ext)) return "📝";
            if (["xls", "xlsx", "csv"].includes(ext)) return "📊";
            if (["zip", "rar"].includes(ext)) return "🗜️";
            if (["txt"].includes(ext)) return "📃";
            return "📁";
          };

          const iconSize =
            enquiryFiles.length > 4
              ? "14px"
              : enquiryFiles.length > 2
              ? "16px"
              : "18px";

          return enquiryFiles.length > 0 ? (
            <ul
              style={{
                display: "flex",
                flexWrap: "wrap",
                paddingLeft: 0,
                margin: 0,
                listStyle: "none",
              }}
            >
              {enquiryFiles.map((file: string, index: number) => (
                <li
                  key={index}
                  style={{
                    width: "33.33%",
                    padding: "6px 8px",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                   <a
                    onClick={(e) => {
                      e.preventDefault();
                      downloadFile(file);
                    }}
                    href="#"
                    title={file}
                    style={{
                      fontSize: iconSize,
                      textDecoration: "none",
                      color: "#007bff",
                      cursor: "pointer",
                    }}
                  >
                    {getFileIcon(file)}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <span style={{ fontSize: "12px", color: "#999" }}>No files</span>
          );
        },
      },
      {
        title: "Reason",
        dataIndex: "reason",
        key: "reason",
        width: 250,
        render: (reason: string, record: any) => {
          const showReason = ["Rework", "In Review", "On Hold"].includes(
            record.status
          );
          return showReason ? (
            <Input.TextArea
              value={reason}
              readOnly
              style={{
                height: "50px",
                overflowY: "auto",
                resize: "none",
              }}
            />
          ) : (
            <span style={{ color: "#ccc", fontSize: "12px" }}>–</span>
          );
        },
      },
    ],
    [employeeNameMap, statusOptions]
  );

  const renderDropdownWithSelectAll = (menus: React.ReactNode, selectedValues: string[], allOptions: any[], filterKey: string) => (
    <div>
      <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
        <Checkbox
          indeterminate={selectedValues.length > 0 && selectedValues.length < allOptions.length}
          checked={selectedValues.length === allOptions.length}
          onChange={(e) => {
            if (e.target.checked) {
              setModalFilters((prev) => ({ ...prev, [filterKey]: allOptions.map((opt) => opt.value) }));
            } else {
              setModalFilters((prev) => ({ ...prev, [filterKey]: [] }));
            }
          }}
        >
          Select all
        </Checkbox>
      </div>
      {menus}
    </div>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Tasks</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Tasks
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
              {empRole?.toLowerCase() !== "employee" && (
                <>
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item
                          key="export-single-task-excel"
                          icon={<FileExcelOutlined />}
                          onClick={() => {
                            if (selectedTaskIds.length > 0) {
                              exportSingleTaskExcel(
                                selectedTaskIds[0],
                                empToken
                              );
                            } else {
                              toast.warn("Please select a task first");
                            }
                          }}
                        >
                          Export Single Task (Excel)
                        </Menu.Item>
                        <Menu.Item
                          key="export-all-tasks-excel"
                          icon={<FileExcelOutlined />}
                          onClick={() => exportAllTasksExcel(empToken)}
                        >
                          Export All Tasks (Excel)
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={["click"]}
                  >
                    <Button
                      type="primary"
                      icon={<ExportOutlined />}
                      className="btn btn-primary d-flex justify-content-center align-items-center p-2 rounded"
                    />
                  </Dropdown>
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    className="btn btn-primary px-4 py-2 rounded d-flex align-items-center"
                    onClick={() => {
                      window.location.href = routes.tmstickets;
                    }}
                  >
                    <span className="w-full text-center block">Add Task</span>
                  </Button>
                </>
              )}
              <div>
                <CollapseHeader />
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Tasks</h5>
              <div className="d-flex my-xl-auto right-content align-items-center ">
                <button
                  className="btn btn-outline-primary me-3 d-flex align-items-center position-relative "
                  onClick={showModal}
                  aria-label={`Open filters${
                    activeFiltersCount > 0
                      ? ` (${activeFiltersCount} active)`
                      : ""
                  }`}
                >
                  <i className="ti ti-filter me-1" aria-hidden="false"></i>
                  Filters
                  {activeFiltersCount > 0 && (
                    <span
                      className="badge bg-primary text-white position-absolute top-0 start-100 translate-middle ms-1 rounded-circle"
                      style={{ fontSize: "0.75em" }}
                    >
                      {activeFiltersCount}
                    </span>
                  )}
                </button>
                <Input.Search
                  placeholder="Search name..."
                  allowClear
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ maxWidth: 300 }}
                />
              </div>
            </div>
            <div className="card-body p-0">
              <Table
                dataSource={filteredTasks}
                columns={taskColumns}
                rowKey="task_id"
                Selection={true}
                onSelectionChange={handleTaskSelectionChange}
                scroll={{ x: 1500 }}
              />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
      </div>
      <div
        className={`modal fade ${filterModalVisible ? "show d-block" : ""}`}
        tabIndex={-1}
        role="dialog"
        aria-labelledby="filterModalLabel"
        aria-hidden={!filterModalVisible}
        key={filterModalVisible ? "filter-open" : "filter-closed"}
        style={{
          display: filterModalVisible ? "block" : "none",
          backgroundColor: filterModalVisible
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
        onClick={() => setFilterModalVisible(false)}
      >
        <div
          className="modal-dialog modal-lg modal-dialog-centered"
          role="document"
          style={{ maxWidth: "60vw", width: "auto" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content" ref={modalRef}>
            <div className="modal-header">
              <h5 className="modal-title" id="filterModalLabel">
                Apply Filters
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setFilterModalVisible(false)}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body p-0">
              <div className="row g-3 px-3 py-3">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Task Name</label>
                  <Select
                    mode="multiple"
                    style={{ width: "100%", color: "#000" }}
                    placeholder="Select Task Name"
                    value={modalFilters.taskName}
                    onChange={(value) =>
                      setModalFilters((prev) => ({ ...prev, taskName: value }))
                    }
                    options={taskNameOptions || []}
                    allowClear
                    showSearch
                    maxTagCount={2}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    getPopupContainer={(triggerNode) =>
                      triggerNode.parentElement || document.body
                    }
                    dropdownStyle={{ maxHeight: 200, overflow: "auto" }}
                    dropdownRender={(menus) =>
                      renderDropdownWithSelectAll(
                        menus,
                        modalFilters.taskName,
                        taskNameOptions,
                        "taskName"
                      )
                    }
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Ticket ID</label>
                  <Select
                    mode="multiple"
                    style={{ width: "100%", color: "#000" }}
                    placeholder="Select Ticket ID"
                    value={modalFilters.ticketId}
                    onChange={(value) =>
                      setModalFilters((prev) => ({ ...prev, ticketId: value }))
                    }
                    options={ticketIdOptions || []}
                    allowClear
                    showSearch
                    maxTagCount={2}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    getPopupContainer={(triggerNode) =>
                      triggerNode.parentElement || document.body
                    }
                    dropdownStyle={{ maxHeight: 200, overflow: "auto" }}
                    dropdownRender={(menus) =>
                      renderDropdownWithSelectAll(
                        menus,
                        modalFilters.ticketId,
                        ticketIdOptions,
                        "ticketId"
                      )
                    }
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Priority</label>
                  <Select
                    mode="multiple"
                    style={{ width: "100%", color: "#000" }}
                    placeholder="Select Priority"
                    value={modalFilters.priority}
                    onChange={(value) =>
                      setModalFilters((prev) => ({ ...prev, priority: value }))
                    }
                    options={priorityOptions || []}
                    allowClear
                    showSearch
                    maxTagCount={2}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    getPopupContainer={(triggerNode) =>
                      triggerNode.parentElement || document.body
                    }
                    dropdownStyle={{ maxHeight: 200, overflow: "auto" }}
                    dropdownRender={(menus) =>
                      renderDropdownWithSelectAll(
                        menus,
                        modalFilters.priority,
                        priorityOptions,
                        "priority"
                      )
                    }
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Category</label>
                  <Select
                    mode="multiple"
                    style={{ width: "100%", color: "#000" }}
                    placeholder="Select Category"
                    value={modalFilters.category}
                    onChange={(value) =>
                      setModalFilters((prev) => ({
                        ...prev,
                        category: value,
                      }))
                    }
                    options={categoryOptions || []} // e.g. [{label: 'Billable', value: 'Billable'}, ...]
                    allowClear
                    showSearch
                    maxTagCount={2}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    getPopupContainer={(triggerNode) =>
                      triggerNode.parentElement || document.body
                    }
                    dropdownStyle={{ maxHeight: 200, overflow: "auto" }}
                    dropdownRender={(menus) =>
                      renderDropdownWithSelectAll(
                        menus,
                        modalFilters.category,
                        categoryOptions,
                        "category"
                      )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-white me-2"
                onClick={clearFilters}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setFilters(modalFilters);
                  setFilterModalVisible(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
      {filterModalVisible && (
        <div
          className="modal-backdrop fade show"
          onClick={() => setFilterModalVisible(false)}
        ></div>
      )}

      <Modal
        title={`Reason for "${selectedStatus}"`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleModalOk}
        okText="Submit"
      >
        <Form layout="vertical">
          <Form.Item
            label="Reason"
            required
            tooltip="This will be saved in backend"
          >
            <Input.TextArea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason"
            />
          </Form.Item>
        </Form>
      </Modal>
      {raisedTicketModalVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => {
            setRaisedTicketModalVisible(false);
            setSelectedTicket(null);
            setSelectedTask(null);
          }}
        >
          <div
            style={{
              width: 900,
              backgroundColor: "#fff",
              borderRadius: 16,
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: "#1e3a8a",
                color: "white",
                padding: "20px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                minHeight: 60,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                  }}
                >
                  📋
                </div>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 18,
                    letterSpacing: "0.5px",
                  }}
                >
                  Task Details & History
                </span>
              </div>
              <button
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                  fontSize: 18,
                  transition: "all 0.3s ease",
                  backdropFilter: "blur(10px)",
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background =
                    "rgba(255, 255, 255, 0.2)";
                  (e.target as HTMLElement).style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background =
                    "rgba(255, 255, 255, 0.1)";
                  (e.target as HTMLElement).style.transform = "scale(1)";
                }}
                onClick={() => {
                  setRaisedTicketModalVisible(false);
                  setSelectedTicket(null);
                  setSelectedTask(null);
                }}
              >
                ✕
              </button>
            </div>
            <div
              style={{
                display: "flex",
                height: "70vh",
                flexDirection: "row",
                background: "white",
                flex: 1,
              }}
            >
              <div
                style={{
                  width: "50%",
                  borderRight: "1px solid #e8eaed",
                  overflowY: "auto",
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                }}
              >
                <div style={{ padding: 20 }}>
                  <div
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      zIndex: 1,
                      paddingBottom: 12,
                      marginBottom: 20,
                      borderBottom: "2px solid #e8eaed",
                    }}
                  >
                    <h5
                      style={{
                        margin: 0,
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#1f2937",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          background:
                            "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          color: "white",
                        }}
                      >
                        ℹ️
                      </span>
                      Ticket Information
                    </h5>
                  </div>
                  {selectedTicket && (
                    <div
                      style={{
                        background: "white",
                        padding: 16,
                        borderRadius: 12,
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            Ticket ID:
                          </span>
                          <span
                            style={{
                              background:
                                "linear-gradient(135deg, #10b981, #059669)",
                              color: "white",
                              padding: "4px 12px",
                              borderRadius: 20,
                              fontSize: 12,
                              fontWeight: 500,
                            }}
                          >
                            {selectedTicket.ticket_id}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#6b7280",
                          }}
                        >
                          Title: {selectedTicket.ticket_title}
                        </div>
                      </div>
                      {selectedTicket.enquiry && (
                        <>
                          <div>
                            <h6
                              style={{
                                margin: "0 0 12px 0",
                                color: "#374151",
                                fontSize: 14,
                                fontWeight: 600,
                              }}
                            >
                              Company Details
                            </h6>
                            <div style={{ display: "grid", gap: 8 }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{ color: "#6b7280", fontSize: 13 }}
                                >
                                  Company:
                                </span>
                                <span
                                  style={{
                                    color: "#111827",
                                    fontSize: 13,
                                    fontWeight: 500,
                                  }}
                                >
                                  {selectedTicket.enquiry.company_name}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{ color: "#6b7280", fontSize: 13 }}
                                >
                                  License:
                                </span>
                                <span
                                  style={{
                                    color: "#111827",
                                    fontSize: 13,
                                    fontWeight: 500,
                                  }}
                                >
                                  {selectedTicket.enquiry.company_license ||
                                    "Not updated"}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{ color: "#6b7280", fontSize: 13 }}
                                >
                                  Email:
                                </span>
                                <span
                                  style={{
                                    color: "#3b82f6",
                                    fontSize: 13,
                                    fontWeight: 500,
                                  }}
                                >
                                  {selectedTicket.enquiry.email ||
                                    "Not updated"}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <h6
                              style={{
                                margin: "0 0 12px 0",
                                color: "#374151",
                                fontSize: 14,
                                fontWeight: 600,
                              }}
                            >
                              Product Information
                            </h6>
                            <div style={{ display: "grid", gap: 8 }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{ color: "#6b7280", fontSize: 13 }}
                                >
                                  Product:
                                </span>
                                <span
                                  style={{
                                    color: "#111827",
                                    fontSize: 13,
                                    fontWeight: 500,
                                  }}
                                >
                                  {productMap[selectedTicket.enquiry.product_id]
                                    ?.name || "Not updated"}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{ color: "#6b7280", fontSize: 13 }}
                                >
                                  Module:
                                </span>
                                <span
                                  style={{
                                    color: "#111827",
                                    fontSize: 13,
                                    fontWeight: 500,
                                  }}
                                >
                                  {moduleMap[
                                    selectedTicket.enquiry.module_id
                                  ] || "Not updated"}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <h6
                              style={{
                                margin: "0 0 12px 0",
                                color: "#374151",
                                fontSize: 14,
                                fontWeight: 600,
                              }}
                            >
                              Requirements
                            </h6>
                            <div
                              style={{
                                background:
                                  "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                border: "1px solid #e2e8f0",
                                padding: 16,
                                borderRadius: 8,
                                maxHeight: 200,
                                overflowY: "auto",
                                fontSize: 13,
                                lineHeight: 1.6,
                                color: "#374151",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {selectedTicket.enquiry.requirement}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div
                style={{
                  width: "50%",
                  overflowY: "auto",
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                }}
              >
                <div style={{ padding: 20 }}>
                  <div
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      zIndex: 1,
                      paddingBottom: 12,
                      marginBottom: 20,
                      borderBottom: "2px solid #e8eaed",
                    }}
                  >
                    <h5
                      style={{
                        margin: 0,
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#1f2937",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          background:
                            "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          color: "white",
                        }}
                      >
                        📅
                      </span>
                      Task Timeline
                    </h5>
                  </div>
                  {selectedTask &&
                  Array.isArray(selectedTask.task_history) &&
                  selectedTask.task_history.length > 0 ? (
                    <div style={{ position: "relative" }}>
                      <div style={{ position: "relative", paddingLeft: 40 }}>
                        <div
                          style={{
                            position: "absolute",
                            left: 20,
                            top: 0,
                            bottom: 0,
                            width: 2,
                            backgroundColor: "#d1d5db",
                            zIndex: 0,
                          }}
                        ></div>
                        {selectedTask.task_history.map((entry, index) => (
                          <div
                            key={index}
                            style={{
                              position: "relative",
                              marginBottom: 24,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 16,
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                left: -27,
                                top: 8,
                                width: 17,
                                height: 17,
                                borderRadius: "50%",
                                background:
                                  entry.status === "Completed"
                                    ? "#10b981"
                                    : entry.status === "On Hold"
                                    ? "#f59e0b"
                                    : entry.status === "In Progress"
                                    ? "#3b82f6"
                                    : entry.status === "In Review"
                                    ? "#8b5cf6"
                                    : entry.status === "Testing"
                                    ? "#06b6d4"
                                    : entry.status === "Testing Completed"
                                    ? "#14b8a6"
                                    : entry.status === "Rework"
                                    ? "#ef4444"
                                    : "#6b7280",
                                border: "3px solid white",
                              }}
                            ></div>
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  background: "white",
                                  padding: "4px 8px",
                                  borderRadius: 16,
                                  fontSize: 11,
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  border: "1px solid #e5e7eb",
                                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                  display: "inline-block",
                                  marginBottom: 8,
                                }}
                              >
                                {moment(entry.entered_at).format(
                                  "MMM DD, hh:mm A"
                                )}
                              </div>
                              <div
                                style={{
                                  background: "white",
                                  padding: 12,
                                  borderRadius: 8,
                                  border: "1px solid #e5e7eb",
                                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginBottom: 8,
                                  }}
                                >
                                  <strong
                                    style={{ fontSize: 13, color: "#374151" }}
                                  >
                                    Status:
                                  </strong>
                                  <span
                                    style={{
                                      background:
                                        entry.status === "Completed"
                                          ? "linear-gradient(135deg, #10b981, #059669)"
                                          : entry.status === "On Hold"
                                          ? "linear-gradient(135deg, #f59e0b, #d97706)"
                                          : entry.status === "In Progress"
                                          ? "linear-gradient(135deg, #3b82f6, #2563eb)"
                                          : entry.status === "In Review"
                                          ? "linear-gradient(135deg, #8b5cf6, #7c3aed)"
                                          : entry.status === "Testing"
                                          ? "linear-gradient(135deg, #06b6d4, #0891b2)"
                                          : entry.status === "Testing Completed"
                                          ? "linear-gradient(135deg, #14b8a6, #0d9488)"
                                          : entry.status === "Rework"
                                          ? "linear-gradient(135deg, #ef4444, #dc2626)"
                                          : "linear-gradient(135deg, #6b7280, #4b5563)",
                                      color: "white",
                                      padding: "4px 12px",
                                      borderRadius: 16,
                                      fontSize: 11,
                                      fontWeight: 500,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    {entry.status}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "grid",
                                    gap: 8,
                                    fontSize: 13,
                                  }}
                                >
                                  {entry.total_hours && (
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <span style={{ color: "#6b7280" }}>
                                        Total Hours:
                                      </span>
                                      <span
                                        style={{
                                          color: "#111827",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {entry.total_hours}
                                      </span>
                                    </div>
                                  )}
                                  {entry.reason && (
                                    <div style={{ marginTop: 8 }}>
                                      <span
                                        style={{
                                          color: "#6b7280",
                                          display: "block",
                                          marginBottom: 4,
                                        }}
                                      >
                                        Reason:
                                      </span>
                                      <div
                                        style={{
                                          background: "#f9fafb",
                                          padding: 8,
                                          borderRadius: 6,
                                          fontSize: 12,
                                          color: "#374151",
                                          border: "1px solid #f3f4f6",
                                        }}
                                      >
                                        {entry.reason}
                                      </div>
                                    </div>
                                  )}
                                  <div
                                    style={{
                                      display: "grid",
                                      gap: 6,
                                      marginTop: 8,
                                      paddingTop: 8,
                                      borderTop: "1px solid #f3f4f6",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <span style={{ color: "#6b7280" }}>
                                        Employee:
                                      </span>
                                      <span
                                        style={{
                                          background:
                                            "linear-gradient(135deg, #06b6d4, #0891b2)",
                                          color: "white",
                                          padding: "2px 8px",
                                          borderRadius: 12,
                                          fontSize: 11,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {employeeNameMap[entry.employee_id] ||
                                          entry.employee_name ||
                                          "-"}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <span style={{ color: "#6b7280" }}>
                                        ID:
                                      </span>
                                      <span
                                        style={{
                                          background:
                                            "linear-gradient(135deg, #ec4899, #db2777)",
                                          color: "white",
                                          padding: "2px 8px",
                                          borderRadius: 12,
                                          fontSize: 11,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {entry.employee_id || "-"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "30px 20px",
                        background: "white",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <div
                        style={{ fontSize: 36, marginBottom: 12, opacity: 0.5 }}
                      >
                        📝
                      </div>
                      <p style={{ color: "#6b7280", margin: 0, fontSize: 14 }}>
                        No task history available for this ticket.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Modal
        title={`Confirm Status Change`}
        open={isConfirmModalVisible}
        onOk={async () => {
          if (confirmTask) {
            await submitStatusChange(
              confirmTask,
              confirmStatus,
              selectedEscalateTo
            );
          }
          setIsConfirmModalVisible(false);
        }}
        onCancel={() => setIsConfirmModalVisible(false)}
        okText="Yes"
        cancelText="No"
      >
        <p>
          Are you sure you want to change the status to{" "}
          <strong>{confirmStatus}</strong>?
        </p>
        {confirmStatus === "Testing Completed" && role === "Employee" && (
          <Form layout="vertical">
            <Form.Item
              label="Escalate To (Manager)"
              required
              rules={[{ required: true, message: "Manager is required" }]}
            >
              <Select
                value={selectedEscalateTo}
                onChange={(value) => setSelectedEscalateTo(value)}
                placeholder="Select Manager"
                options={filteredEmployees.map((emp) => ({
                  label: `${emp.firstname} ${emp.lastname}`,
                  value: emp.employee_id,
                }))}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};
export default Tasks;
